import { useMemo } from "react";
import { useModal } from "@/contexts/ModalContext";
import { UnsplashAttribution } from "@/components/ui/UnsplashAttribution";
import { MediaItem, normalizeMedia } from "@/types/media.types";

const MAX_VISIBLE = 4;

interface MediaCarouselProps {
  /** Accetta anche il formato legacy (array di URL). */
  media: MediaItem[] | string[];
  className?: string;
  imageSize?: string;
}

export const MediaCarousel = ({ media: rawMedia, className = "", imageSize = "w-20 h-20" }: MediaCarouselProps) => {
  const { openModal } = useModal();
  const media = useMemo(() => normalizeMedia(rawMedia), [rawMedia]);

  if (media.length === 0) return null;

  const visibleMedia = media.slice(0, MAX_VISIBLE);
  const remaining = media.length - MAX_VISIBLE;

  return (
    <div className={`flex mx-1 gap-2 flex-wrap items-center ${className}`}>
      {visibleMedia.map((item, index) => (
        <div key={`${item.url}-${index}`} className="relative flex-shrink-0 overflow-hidden rounded-xl">
          <img
            src={item.url}
            alt={item.alt || `Media ${index + 1}`}
            className={`${imageSize} object-cover rounded-xl cursor-pointer transition-all duration-200 hover:brightness-75 hover:scale-105 hover:shadow-md`}
            onClick={() => openModal('media-carousel', { media, initialIndex: index })}
          />
          <UnsplashAttribution item={item} />
        </div>
      ))}
      {remaining > 0 && (
        <button
          className="w-14 h-14 md:w-32 md:h-32 rounded-xl flex-shrink-0 bg-gray-100 border border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
          onClick={() => openModal('media-carousel', { media, initialIndex: MAX_VISIBLE })}
          aria-label={`Show ${remaining} more photos`}
        >
          <h4 className="text-gray-700 text-xs md:text-base font-semibold">+{remaining}</h4>
          <span className="text-[10px] md:text-sm text-gray-500 mt-0.5 hidden md:inline">foto</span>
        </button>
      )}
    </div>
  );
};
